import { Component, Input, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import PresignedUrlRequest from 'app/models/typescript/PresignedUrlRequest';
import { PriorityViewModel } from 'app/models/PriorityViewModel';
import { ReportingSiteService } from '../../../services/reporting-site.service';
import Utils from '../../../shared/utils';
import CustomValidators from '../../../shared/custom-validators';
import { finalize } from 'rxjs/operators';
import { S3Buckets } from 'app/models/S3Buckets';

@Component({
  selector: 'app-components-manager-reportingcustomizations',
  templateUrl: './ReportingCustomizations.component.html'
})
export class ReportingCustomizationsComponent implements OnInit {
  @Input() partnerId: number;
  reportingSite: Boo.Objects.ReportingSite;
  isLoading: boolean;
  isLegacyCollapsed: boolean = true;
  isBoostabilityOrManta: boolean = false;
  privacyPolicyUrlControl: FormControl<string>;
  // Toggle this to control whether uploads/clears auto-save or require the Save button.
  private autoSaveOnUpload: boolean = false;

  // murder: Remove chartColors and chartColorValidation after MMI v3 rollout — ChartColors is deprecated in v3.
  chartColors: { Color: KnockoutObservable<string>; Index: KnockoutObservable<number>; original: Boo.Objects.ReportingSiteChartColor }[] = [];
  private chartColorValidation: KnockoutObservable<any> = ko.validatedObservable(undefined);

  constructor(private reportingSiteService: ReportingSiteService) { }

  ngOnInit(): void {
    this.isBoostabilityOrManta = this.partnerId === Boo.Objects.Enums.PartnerEnum.Boostability
      || this.partnerId === Boo.Objects.Enums.PartnerEnum.Manta;
    this.isLoading = true;
    this.reportingSiteService.get(this.partnerId)
      .pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: (reportingSite: Boo.Objects.ReportingSite) => {
          this.reportingSite = reportingSite;
          this.privacyPolicyUrlControl = new FormControl(reportingSite.PrivacyPolicyUrl || '', [
            CustomValidators.validUrl(),
            Validators.maxLength(512)
          ]);
          this.setupChartColorValidation();
        },
        error: (err: string) => toastr.error(err)
      });
  }

  // murder: Remove after MMI v3 rollout — ChartColors is deprecated in v3.
  setupChartColorValidation(): void {
    const colors = this.reportingSite.ChartColors.map(x => {
      const color = ko.observable(x.Color);
      const index = ko.observable(x.Index);
      color.extend({
        required: { message: 'Chart color is required.' },
        validation: [{
          validator: (val: string): any => {
            return val.indexOf('rgba') >= 0;
          },
          message: 'Chart color must be rgba'
        }]
      });
      return { Color: color, Index: index, original: x };
    });
    this.chartColors = colors;
    this.chartColorValidation = ko.validatedObservable(colors);
  }

  uploadLightModeLogo(): void {
    const request = new PresignedUrlRequest();
    request.BucketName = S3Buckets.s3staticfiles;
    request.Folder = 'reportinglogos/' + this.partnerId;
    request.UseUniqueFileName = true;

    PriorityViewModel.show('app-components-shared-uploadfile', { preSignedURLRequest: request })
      .then((fileData: any) => {
        this.reportingSite.LightModeLogoUrl = fileData.FullUrl;
        if (this.autoSaveOnUpload) {
          this.save();
        }
      });
  }

  uploadDarkModeLogo(): void {
    const request = new PresignedUrlRequest();
    request.BucketName = S3Buckets.s3staticfiles;
    request.Folder = 'reportinglogos/' + this.partnerId;
    request.UseUniqueFileName = true;

    PriorityViewModel.show('app-components-shared-uploadfile', { preSignedURLRequest: request })
      .then((fileData: any) => {
        this.reportingSite.DarkModeLogoUrl = fileData.FullUrl;
        if (this.autoSaveOnUpload) {
          this.save();
        }
      });
  }

  // murder: Remove after MMI v3 rollout — LoadingIconUrl is deprecated in v3.
  uploadLoadingIcon(): void {
    const request = new PresignedUrlRequest();
    request.BucketName = S3Buckets.s3staticfiles;
    request.Folder = 'loadingicons/' + this.partnerId;
    request.UseUniqueFileName = true;

    PriorityViewModel.show('app-components-shared-uploadfile', { preSignedURLRequest: request })
      .then((fileData: any) => {
        this.reportingSite.LoadingIconUrl = fileData.FullUrl;
        if (this.autoSaveOnUpload) {
          this.save();
        }
      });
  }

  save(): JQueryPromise<void> {
    this.privacyPolicyUrlControl.markAsDirty();
    this.privacyPolicyUrlControl.updateValueAndValidity();
    if (this.privacyPolicyUrlControl.invalid) {
      toastr.error('Privacy Policy URL is not a valid URL.');
      return;
    }
    this.reportingSite.PrivacyPolicyUrl = this.privacyPolicyUrlControl.value;

    // murder: Remove chartColorValidation check after MMI v3 rollout.
    if (!this.chartColorValidation.isValid()) {
      this.chartColorValidation.errors.showAllMessages();
      return;
    }

    // Sync knockout-wrapped chart color values back to the plain object before saving.
    // murder: Remove after MMI v3 rollout.
    this.chartColors.forEach(c => c.original.Color = c.Color());

    return Utils.wrapDfd(this.reportingSiteService.save(this.reportingSite))
      .then((reportingSite: Boo.Objects.ReportingSite) => {
        this.reportingSite.ReportingSiteId = reportingSite.ReportingSiteId;
        // murder: Remove ChartColors refresh after MMI v3 rollout.
        this.reportingSite.ChartColors = reportingSite.ChartColors;
        this.setupChartColorValidation();
        toastr.success('Reporting customizations saved.');
      })
      .fail((displayMessage: string) => {
        toastr.error(displayMessage);
      });
  }
}
